/**
 * Double Row Template Calculations
 *
 * Template-specific calculation utilities for double-row hangars
 */

import type { DoubleRowParams } from './params'
import { getEffectiveRowB, calculateBuildingDepth } from './params'
import {
  calculateUnitWidthsWithOverrides,
  calculateUnitOffsets,
  type WidthCalculationResult,
} from '../base'

/**
 * Extended result with suggested fixes
 */
export interface DoubleRowCalculationResult {
  /** Row A calculation result */
  rowA: WidthCalculationResult
  /** Row B calculation result */
  rowB: WidthCalculationResult
  /** Whether both rows are balanced */
  isBalanced: boolean
  /** Total building depth (Y-axis) */
  buildingDepth: number
  /** Suggested fixes for layout issues */
  suggestedFixes: SuggestedFix[]
}

/**
 * Suggested fix for layout imbalance
 */
export interface SuggestedFix {
  type: 'expand_building' | 'redistribute' | 'reduce_count' | 'reduce_unit'
  row: 'A' | 'B' | 'both'
  label: string
  description: string
  newValue?: number
}

/**
 * Calculate unit layout for both rows in double-row template
 */
export function calculateDoubleRowLayout(params: DoubleRowParams): DoubleRowCalculationResult {
  const rowB = getEffectiveRowB(params)

  // Calculate Row A
  const rowAResult = calculateUnitWidthsWithOverrides(
    params.width_ft,
    params.row_a.unit_count,
    params.partition_wall_thickness_ft,
    params.row_a_overrides?.filter(o => o.width_ft !== undefined).map(o => ({
      index: o.index,
      width_ft: o.width_ft!,
    }))
  )

  // Calculate Row B
  const rowBResult = calculateUnitWidthsWithOverrides(
    params.width_ft,
    rowB.unit_count,
    params.partition_wall_thickness_ft,
    params.row_b_overrides?.filter(o => o.width_ft !== undefined).map(o => ({
      index: o.index,
      width_ft: o.width_ft!,
    }))
  )

  // Calculate building depth
  const buildingDepth = calculateBuildingDepth(params)

  // Generate suggested fixes if not balanced
  const suggestedFixes: SuggestedFix[] = []

  if (!rowAResult.isBalanced) {
    suggestedFixes.push({
      type: 'expand_building',
      row: 'A',
      label: 'Expand Building',
      description: `Increase building length to ${Math.ceil(rowAResult.suggestedBuildingLength)} ft`,
      newValue: Math.ceil(rowAResult.suggestedBuildingLength),
    })
    suggestedFixes.push({
      type: 'redistribute',
      row: 'A',
      label: 'Redistribute Row A',
      description: 'Clear Row A overrides and distribute equally',
    })
  }

  if (!rowBResult.isBalanced) {
    // Only add if not already suggested for Row A (use same building length)
    if (!suggestedFixes.some(f => f.type === 'expand_building')) {
      suggestedFixes.push({
        type: 'expand_building',
        row: 'B',
        label: 'Expand Building',
        description: `Increase building length to ${Math.ceil(rowBResult.suggestedBuildingLength)} ft`,
        newValue: Math.ceil(rowBResult.suggestedBuildingLength),
      })
    }
    suggestedFixes.push({
      type: 'redistribute',
      row: 'B',
      label: 'Redistribute Row B',
      description: 'Clear Row B overrides and distribute equally',
    })
  }

  return {
    rowA: rowAResult,
    rowB: rowBResult,
    isBalanced: rowAResult.isBalanced && rowBResult.isBalanced,
    buildingDepth,
    suggestedFixes,
  }
}

/**
 * Calculate unit offsets for a row
 */
export function calculateRowUnitOffsets(
  widths: number[],
  wallThickness: number
): number[] {
  return calculateUnitOffsets(widths, wallThickness)
}

/**
 * Get Y offset for a row's units
 * Row A: starts after Row B + shared wall
 * Row B: starts at 0
 */
export function getRowYOffset(
  rowId: 'A' | 'B',
  params: DoubleRowParams
): number {
  if (rowId === 'B') {
    return 0
  }
  // Row A starts after Row B + shared wall
  const rowB = getEffectiveRowB(params)
  return rowB.depth_ft + params.shared_wall_thickness_ft
}
